import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, App, Button, Empty, Progress, Result, Select, Space, Statistic, Table, Tag, theme, Typography } from "antd";
import type { PackScanResult } from "@shoe-trace/shared";
import { findOrder, getOrder, type OutboundOrder } from "../api/client";
import { AdminScanNotice, ScanBox } from "../components/widgets";
import {
  describeVerifyOutcome,
  listOpenOrders,
  orderReadyToShip,
  orderStatusLabel,
  passFinished,
  playScanTone,
  SHIP_DONE,
  submitShipOrder,
  submitVerifyScan,
} from "../scan/actions";

export function PackingStation(): JSX.Element {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OutboundOrder[]>([]);
  const [order, setOrder] = useState<OutboundOrder | null>(null);
  const [result, setResult] = useState<PackScanResult | null>(null);
  const [held, setHeld] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [shippedLines, setShippedLines] = useState<string[] | null>(null);

  const load = useCallback(() => {
    void listOpenOrders().then(setOrders);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const verified = order?.verifiedQty ?? 0;
  const required = order?.totalQty ?? 0;
  const blocked = held && result?.status === "BLOCKED";

  async function bind(orderNo: string): Promise<void> {
    const next = await getOrder(orderNo);
    if (next.status === "SHIPPED") {
      setOrder(null);
      setResult(null);
      setHeld(false);
      setBindError(null);
      setHighlight(null);
      setShippedLines([SHIP_DONE]);
      load();
      return;
    }
    if (orderReadyToShip(next)) {
      const shipped = await submitShipOrder(next.orderNo);
      if (!shipped.ok) {
        setBindError(shipped.message);
        return;
      }
      message.success(SHIP_DONE);
      setOrder(null);
      setResult(null);
      setHeld(false);
      setBindError(null);
      setHighlight(null);
      setShippedLines(null);
      load();
      return;
    }
    setOrder(next);
    setResult(null);
    setHeld(false);
    setBindError(null);
    setHighlight(null);
    setShippedLines(null);
    message.success(`已对上出库单 ${next.orderNo}。现在可以逐双扫描。`);
  }

  return (
    <div>
      <AdminScanNotice />
      {orders.length === 0 && !order ? (
        <Empty description="没有待复核的出库单。请主管先导入出库清单。" />
      ) : null}
      <Button type="link" style={{ paddingLeft: 0 }} onClick={() => navigate("/scan")}>
        去扫码工作台逐只复核
      </Button>
      <Space className="no-print" style={{ marginBottom: 12 }} wrap>
        <Select
          style={{ width: 420 }}
          placeholder="选择还没出库的出库单"
          value={order?.orderNo}
          options={orders.map((row) => ({
            value: row.orderNo,
            label: `${row.orderNo} · ${row.lineCount} 个 SKU · ${row.channel}`,
          }))}
          onChange={(orderNo) => {
            void bind(orderNo).catch((error: unknown) => {
              setBindError(error instanceof Error ? error.message : "没有这张出库单。");
            });
          }}
        />
        {order ? (
          <>
            <span>{order.orderNo}</span>
            <span>{order.channel}</span>
            <span>{order.targetWarehouse}</span>
            <Tag>{orderStatusLabel(order.status)}</Tag>
          </>
        ) : null}
      </Space>
      <ScanBox
        placeholder={order ? "扫描鞋盒外标或鞋身吊牌" : "扫描出库单号"}
        disabled={blocked}
        onSubmit={async (code) => {
          if (!order) {
            try {
              const found = await findOrder(code);
              await bind(found.orderNo);
            } catch (error: unknown) {
              setBindError(error instanceof Error ? error.message : "没有这张出库单。请核对单号，或让主管先导入清单。");
            }
            return;
          }
          setBindError(null);
          const submitted = await submitVerifyScan(order.orderNo, code);
          if (!submitted.ok) {
            message.error(submitted.message);
            return;
          }
          playScanTone(submitted.result.status === "PASS" ? "pass" : "block");
          if (submitted.result.status === "PASS" && passFinished(submitted.result)) {
            setOrder(null);
            setResult(null);
            setHeld(false);
            setHighlight(null);
            setShippedLines([submitted.result.peelHint, SHIP_DONE]);
            load();
            return;
          }
          if (!submitted.order) {
            message.error("这一扫没有记上。");
            return;
          }
          setShippedLines(null);
          setOrder(submitted.order);
          setResult(submitted.result);
          setHeld(submitted.result.status === "BLOCKED");
          if (submitted.result.status === "PASS") setHighlight(submitted.result.humanCode);
          if (submitted.result.status === "PASS") message.success("这双已复核。");
        }}
      />
      {bindError ? <Alert type="error" showIcon message={bindError} /> : null}
      {order ? (
        <div style={{ maxWidth: 640, margin: "0 auto 16px" }}>
          <Statistic title="已复核" value={verified} suffix={`/ 计划 ${required} 双`} />
          <Progress percent={required === 0 ? 0 : Math.round((verified / required) * 100)} status={verified > 0 && verified >= required ? "success" : "active"} />
          <Table
            style={{ marginTop: 12 }}
            size="small"
            rowKey="skuId"
            pagination={{ pageSize: 50 }}
            dataSource={order.lines}
            rowClassName={(row) => (row.humanCode === highlight ? "ant-table-row-selected" : "")}
            columns={[
              { title: "SKU", dataIndex: "humanCode" },
              { title: "计划双数", dataIndex: "requiredQty" },
              { title: "已复核", dataIndex: "verifiedQty" },
            ]}
          />
        </div>
      ) : null}
      <ResultPanel
        result={result}
        held={held}
        shippedLines={shippedLines}
        onRelease={() => { setHeld(false); setResult(null); }}
      />
    </div>
  );
}

function ResultPanel({
  result,
  held,
  shippedLines,
  onRelease,
}: {
  result: PackScanResult | null;
  held: boolean;
  shippedLines: string[] | null;
  onRelease: () => void;
}): JSX.Element {
  const { token } = theme.useToken();
  if (shippedLines) {
    return (
      <div
        style={{
          minHeight: 280,
          padding: 32,
          borderRadius: token.borderRadiusLG,
          background: token.colorSuccessBg,
          border: `4px solid ${token.colorSuccess}`,
        }}
      >
        <Result status="success" title="这张出库单已出库" />
        {shippedLines.map((line, index) => (
          <Typography.Paragraph key={`${index}-${line}`}>{line}</Typography.Paragraph>
        ))}
      </div>
    );
  }
  if (!result || (result.status === "BLOCKED" && !held)) {
    return (
      <div style={{ minHeight: 280, padding: 32, borderRadius: token.borderRadiusLG }}>
        <Empty description="扫一双，复核一双。先扫外标，复核过后再放进箱子。" />
      </div>
    );
  }
  const view = describeVerifyOutcome(result);
  const pass = view.tone === "ok";
  return (
    <div
      style={{
        minHeight: 280,
        padding: 32,
        borderRadius: token.borderRadiusLG,
        background: pass ? token.colorSuccessBg : token.colorErrorBg,
        border: `4px solid ${pass ? token.colorSuccess : token.colorError}`,
      }}
    >
      <Result
        status={pass ? "success" : "error"}
        title={view.title}
        subTitle={pass ? "撕下鞋盒外标，贴到外箱内侧襟翼。鞋盒外面不要留这张标。" : undefined}
      />
      {view.summary ? <Typography.Paragraph>{view.summary}</Typography.Paragraph> : null}
      {view.emphasis.map((line, index) => (
        <Typography.Title key={`${index}-${line}`} level={2} style={{ color: token.colorError, marginTop: 0 }}>
          {line}
        </Typography.Title>
      ))}
      {view.lines.map((line, index) => (
        <Typography.Paragraph key={`${index}-${line}`}>{line}</Typography.Paragraph>
      ))}
      {view.hold ? (
        <Button type="primary" onClick={onRelease}>
          我已放回这双，继续复核
        </Button>
      ) : null}
    </div>
  );
}
